/**
 * OncologyEpisodeSelector Component
 * 
 * A component for selecting an oncology episode to link when creating a new encounter.
 * Used during admission/encounter creation to pre-select an episode.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getPatientEpisodes, EPISODE_STATUS } from '../../services/episodeOfCareService';

const OncologyEpisodeSelector = ({
  patientId,
  facilityId,
  selectedEpisodeId = '',
  onChange, // (episodeId) => void
  disabled = false,
  size = 'small',
  showLabel = true
}) => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch patient's oncology episodes
  const fetchEpisodes = useCallback(async () => {
    if (!patientId || !facilityId) {
      setEpisodes([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await getPatientEpisodes(patientId, facilityId, { limit: 50 });
      // Only show active or on-hold episodes
      const activeEpisodes = (result.data || []).filter(
        ep => ep.status === EPISODE_STATUS.ACTIVE || ep.status === EPISODE_STATUS.ON_HOLD
      );
      setEpisodes(activeEpisodes);
    } catch (err) {
      console.error('Error fetching patient episodes:', err);
      setError('Failed to load oncology episodes');
      setEpisodes([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, facilityId]);

  useEffect(() => {
    fetchEpisodes();
  }, [fetchEpisodes]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  // Don't show anything if patient has no oncology episodes
  if (!loading && episodes.length === 0) {
    return null;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      
      <FormControl fullWidth size={size} disabled={disabled || loading}>
        {showLabel && (
          <InputLabel id="oncology-episode-select-label">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimelineIcon sx={{ fontSize: 16 }} />
              Link to Oncology Episode
            </Box>
          </InputLabel>
        )}
        <Select
          labelId="oncology-episode-select-label"
          id="oncology-episode-select"
          value={selectedEpisodeId}
          onChange={(e) => onChange?.(e.target.value)}
          label={showLabel ? "Link to Oncology Episode" : undefined}
          startAdornment={loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
        >
          <MenuItem value="">
            <em>None - No oncology episode link</em>
          </MenuItem>
          {episodes.map((episode) => (
            <MenuItem key={episode.id} value={episode.id}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {episode.episodeName || 'Oncology Episode'}
                  </Typography>
                  <Chip
                    size="small"
                    label={episode.status}
                    color={episode.status === 'Active' ? 'success' : 'warning'}
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                  <Chip
                    size="small"
                    label={episode.currentPhase}
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {episode.diagnosisSummary} • Started: {formatDate(episode.startDate)}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {episodes.length > 0 && !selectedEpisodeId && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Patient has {episodes.length} active oncology episode{episodes.length > 1 ? 's' : ''} available for linking
        </Typography>
      )}
    </Box>
  );
};

export default OncologyEpisodeSelector;
